import {
  AuthMethod,
  BillingStatus,
  Company_Create,
  Company_Read as CompanyProto,
  Company_Update,
  CompanyType,
  Currency,
  DateFormat,
  Language,
  SaasBillingPlan,
  SystemOfMeasure,
  TimeFormat,
  TimeZone,
  UnitOfDistance,
  UsageStatus,
  WorkWeekStartDay,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { data as enums, DEFAULT_COUNTRY_CODE } from '~constants/enums';
import { AddressFormValue, AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyBasic } from '~hooks/useCompany/models';
import {
  CompanyContactFormValue,
  ContactItem,
} from '~hooks/useContact/models/ContactItem';
import { Nullable } from '~types/Nullable';

interface UnitOfDistanceFormValue {
  value: UnitOfDistance;
}

interface AdminCompanyDeparseInput {
  SaasBillingPlan?: Nullable<SaasBillingPlan>;
  address?: Nullable<AddressItem | AddressFormValue>;
  authMethods?: Array<AuthMethod>;
  billingContact?: Nullable<ContactItem | CompanyContactFormValue>;
  billingStatus?: Nullable<BillingStatus>;
  companyType?: Nullable<CompanyType>;
  currency: Currency;
  dateFormat: DateFormat;
  dbaName?: string;
  defaultLat: Nullable<number>;
  defaultLon: Nullable<number>;
  doingBusinessName?: string;
  externalStripeCustomerId?: Nullable<string>;
  id?: string;
  legalName: string;
  // `parent_company_id` is permitted on create only — CompanyService::Update drops it,
  // so changing a company's parent from Admin > Companies does nothing on edit.
  parentCompany?: Nullable<CompanyBasic> | { id?: string | null } | null;
  primaryContact?: Nullable<ContactItem | CompanyContactFormValue>;
  saasBillingPlan?: Nullable<SaasBillingPlan>;
  systemOfMeasure: SystemOfMeasure;
  timeFormat: TimeFormat;
  timeZone: Nullable<TimeZone>;
  unitOfDistance: UnitOfDistance | UnitOfDistanceFormValue;
  usageStatus?: Nullable<UsageStatus>;
  workWeekStartDay?: WorkWeekStartDay | { value: string } | null;
}

const resolveWorkWeekStartDay = (
  workWeekStartDay: AdminCompanyDeparseInput['workWeekStartDay'],
): WorkWeekStartDay | undefined => {
  if (!workWeekStartDay) return undefined;
  if (typeof workWeekStartDay === 'string') return workWeekStartDay;
  return workWeekStartDay.value as WorkWeekStartDay;
};

const resolveUnitOfDistance = (
  unitOfDistance: AdminCompanyDeparseInput['unitOfDistance'],
) => {
  if (typeof unitOfDistance === 'string') return unitOfDistance;
  return unitOfDistance.value;
};

export class AdminCompany {
  public static parse(proto: CompanyProto): AdminCompany {
    return new AdminCompany(
      proto?.id ?? '',
      proto?.created_at ?? null,
      proto.parent_company ? CompanyBasic.parse(proto.parent_company) : null,
      proto.legal_name ?? '',
      proto?.dba_name ?? '',
      proto?.address ? AddressItem.parse(proto.address) : null,
      proto.company_type ?? null,
      proto?.tread_customer ?? '',
      proto?.language ?? enums.language.default,
      proto?.date_format ?? enums.date_format.default,
      proto?.time_format ?? enums.time_format.default,
      proto?.system_of_measure ?? enums.system_of_measure.default,
      proto?.unit_of_distance ?? enums.unit_of_distance.default,
      proto.currency ?? enums.currency.default,
      proto?.time_zone ?? enums.time_zone.default,
      proto?.primary_contact ? ContactItem.parse(proto.primary_contact) : null,
      proto?.billing_contact ? ContactItem.parse(proto.billing_contact) : null,
      proto.logo_url ?? null,
      proto.logo_preview_url ?? null,
      proto?.usage_status ?? enums.usage_status.default,
      proto?.billing_status ?? enums.billing_status.default,
      proto?.saas_billing_plan ?? enums.saas_billing_plan.default,
      proto.auth_methods,
      proto.tread_id,
      proto.auto_complete_order,
      isNil(proto?.default_lat) ? null : Number(proto.default_lat),
      isNil(proto?.default_lon) ? null : Number(proto?.default_lon),
      proto?.work_week_start_day ?? enums.work_week_start_day.default,
      proto.external_stripe_customer_id ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparse(
    proto: AdminCompanyDeparseInput,
  ): Company_Create | Company_Update {
    const isCreatingCompany = !proto.id;

    const data = {
      parent_company_id: proto.parentCompany?.id,
      legal_name: proto.legalName,
      dba_name: proto.doingBusinessName ?? proto.dbaName,
      // `deparseUpdate`, not `deparse`: the admin form hands us a plain object, and
      // `deparse` builds a read-shaped payload whose required `id` the form value has
      // no way to supply. `accepts_nested_attributes_for :address, update_only: true`
      // means the address id is never needed.
      address: AddressItem.deparseUpdate(proto.address),
      company_type: proto.companyType,
      primary_contact: proto.primaryContact
        ? ContactItem.deparseCompany(proto.primaryContact)
        : undefined,
      billing_contact: proto.billingContact
        ? ContactItem.deparseCompany(proto.billingContact)
        : undefined,
      // `Company_Create` requires both, but the admin form renders neither — so they
      // are defaults for a NEW company only. Sending them on update reset every
      // non-English company's language back to "en" and forced tread_customer true,
      // the same clobbering `Company.deparseUpdate` was already fixed for.
      tread_customer: isCreatingCompany ? true : undefined,
      language: isCreatingCompany ? enums.language.default : undefined,
      time_format: proto.timeFormat,
      date_format: proto.dateFormat,
      system_of_measure: proto.systemOfMeasure,
      unit_of_distance: resolveUnitOfDistance(proto.unitOfDistance),
      currency: proto.currency,
      time_zone: proto.timeZone ?? enums.time_zone.default,
      usage_status: proto.usageStatus || enums.usage_status.default,
      billing_status: proto.billingStatus || enums.billing_status.default,
      saas_billing_plan:
        proto.SaasBillingPlan ?? proto.saasBillingPlan ?? enums.saas_billing_plan.default,
      default_lat: isNil(proto.defaultLat) ? null : proto.defaultLat.toString(),
      default_lon: isNil(proto.defaultLon) ? null : proto.defaultLon.toString(),
      work_week_start_day: resolveWorkWeekStartDay(proto.workWeekStartDay),
    };

    if (!isCreatingCompany) {
      const updateAdminCompanyData = data as Company_Update;
      updateAdminCompanyData.auth_methods = proto.authMethods;
      updateAdminCompanyData.external_stripe_customer_id =
        proto.externalStripeCustomerId?.trim() || null;
    }

    return data as Company_Create | Company_Update;
  }

  public get id(): string {
    return this._id;
  }

  public get createdAt(): string {
    return this._created_at;
  }

  public get legalName(): string {
    return this._legal_name;
  }

  public get parentCompany(): Nullable<CompanyBasic> {
    return this._parentCompany;
  }

  public get dbaName(): string {
    return this._dba_name;
  }

  public get isTreadCustomer(): boolean {
    return this._tread_customer;
  }

  public get address(): Nullable<AddressItem> {
    return this._address;
  }

  public get primaryContact(): Nullable<ContactItem> {
    return this._primary_contact;
  }

  public get billingContact(): Nullable<ContactItem> {
    return this._billing_contact;
  }

  public get timeZone() {
    return this._time_zone;
  }

  public get companyType(): Nullable<CompanyType> {
    return this._company_type;
  }
  public get language(): Language {
    return this._language;
  }

  public get timeFormat() {
    return this._time_format;
  }

  public get dateFormat() {
    return this._date_format;
  }

  public get unitOfDistance() {
    return this._unit_of_distance;
  }
  public get systemOfMeasure() {
    return this._system_of_measure;
  }

  public get currency() {
    return this._currency;
  }
  public get logoUrl(): Nullable<string> {
    return this._logo_url;
  }

  public get logoPreviewUrl(): Nullable<string> {
    return this._logo_preview_url;
  }

  public get usageStatus(): Nullable<UsageStatus> {
    return this._usage_status;
  }

  public get billingStatus(): Nullable<BillingStatus> {
    return this._billing_status;
  }

  public get saasBillingPlan(): Nullable<SaasBillingPlan> {
    return this._saas_billing_plan;
  }

  public get country(): string {
    return this._address?.country?.code ?? DEFAULT_COUNTRY_CODE;
  }

  public get authMethods() {
    return this._auth_methods;
  }

  public get treadId(): string {
    return this._tread_id;
  }

  public get autoCompleteOrder() {
    return this._auto_complete_order;
  }

  public get defaultLat(): Nullable<number> {
    return this._default_lat;
  }

  public get defaultLon(): Nullable<number> {
    return this._default_lon;
  }

  public get workWeekStartDay() {
    return this._work_week_start_day;
  }

  public get externalStripeCustomerId() {
    return this._external_stripe_customer_id;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _created_at: string,
    private _parentCompany: Nullable<CompanyBasic>,

    private _legal_name: string,
    private _dba_name: string,
    private _address: Nullable<AddressItem>,

    private _company_type: CompanyType,

    private _tread_customer: boolean,

    private _language: Language,
    private _date_format: DateFormat,
    private _time_format: TimeFormat,
    private _system_of_measure: SystemOfMeasure,
    private _unit_of_distance: UnitOfDistance,
    private _currency: Currency,
    private _time_zone: TimeZone,

    private _primary_contact: Nullable<ContactItem>,
    private _billing_contact: Nullable<ContactItem>,
    private _logo_url: Nullable<string>,
    private _logo_preview_url: Nullable<string>,
    private _usage_status: Nullable<UsageStatus>,
    private _billing_status: Nullable<BillingStatus>,
    private _saas_billing_plan: Nullable<SaasBillingPlan>,
    private _auth_methods: Array<AuthMethod>,
    private _tread_id: string,
    private _auto_complete_order: boolean,

    private _default_lat: Nullable<number>,
    private _default_lon: Nullable<number>,
    private _work_week_start_day: WorkWeekStartDay,
    private _external_stripe_customer_id: Nullable<string> = null,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}
}
